import { exhaustiveCheck } from '../../lib/util';
import type { EventOrLeg, Events } from './TimeData';
import {
  isCoaEvent,
  isLeg,
  isOffHireEvent,
  isPortCallEvent,
  isSingleEvent,
  isSpotEvent,
  isTCOrderEvent,
  isTimeData,
  isWait,
  isStaticEvent,
} from './TimeData';
import type { VoyageGroup } from './Timelines';

export function isEqual(ev1: EventOrLeg<Events>, ev2: EventOrLeg<Events>) {
  if (isLeg(ev1)) {
    if (!isLeg(ev2)) {
      return false;
    }
    if (ev1.start !== ev2.start || ev1.end !== ev2.end) {
      return false;
    }
    if (ev1.from.id !== ev1.from.id || ev1.to.id !== ev2.to.id) {
      return false;
    }
  } else if (isWait(ev1)) {
    if (!isWait(ev2)) {
      return false;
    }
    if (ev1.start !== ev2.start || ev1.end !== ev2.end) {
      return false;
    }
    if (ev1.event.locationId !== ev2.event.locationId) {
      return false;
    }
  } else if (isTimeData(ev1)) {
    if (!isTimeData(ev2)) {
      return false;
    }
    if (ev1.start !== ev2.start || ev1.end !== ev2.end) {
      return false;
    }
    if (isTCOrderEvent(ev1.event)) {
      if (!isTCOrderEvent(ev2.event)) {
        return false;
      }
      if (
        ev1.event.type !== ev2.event.type ||
        ev1.event.order.id !== ev2.event.order.id ||
        ev1.event.locationId !== ev2.event.locationId
      ) {
        return false;
      }
    } else if (isSingleEvent(ev1.event)) {
      if (!isSingleEvent(ev2.event)) {
        return false;
      }
      if (ev1.event.locationId !== ev2.event.locationId) {
        return false;
      }
    } else if (isOffHireEvent(ev1.event)) {
      if (!isOffHireEvent(ev2.event)) {
        return false;
      }
      if (
        ev1.event.locationAId !== ev2.event.locationAId ||
        ev1.event.locationBId !== ev2.event.locationBId
      ) {
        return false;
      }
    } else if (isSpotEvent(ev1.event)) {
      if (!isSpotEvent(ev2.event)) {
        return false;
      }
    } else if (isCoaEvent(ev1.event)) {
      if (!isCoaEvent(ev2.event)) {
        return false;
      }
    } else if (isPortCallEvent(ev1.event)) {
      if (!isPortCallEvent(ev2.event)) {
        return false;
      }
      if (ev1.event.portCallId !== ev2.event.portCallId) {
        return false;
      }
    } else if (isStaticEvent(ev1.event)) {
      if (!isStaticEvent(ev2.event)) {
        return false;
      }

      if (ev1.event.entry.clientId !== ev2.event.entry.clientId) {
        return false;
      }
    } else {
      exhaustiveCheck(ev1.event);
    }
  } else {
    exhaustiveCheck(ev1);
  }

  return true;
}

export function squashVoyageGroup(vg: VoyageGroup<Events>[]) {
  if (vg.length === 1) {
    return vg[0].eventsAndLegs;
  } else {
    return vg
      .flatMap((vg) => vg.eventsAndLegs)
      .sort((ev1, ev2) => ev1.start - ev2.start);
  }
}
export function getEventsDifferenceTimerange(
  ...eventsPack: EventOrLeg<Events>[][]
) {
  if (eventsPack.length === 0 || eventsPack.length === 1) {
    return null;
  }

  const eventsMinimalCommonLength = Math.min(
    ...eventsPack.map((events) => events.length),
  );

  let diffStartedAt = Infinity;

  const firstEventsFromPack = eventsPack[0];
  const restOfEventsPack = eventsPack.slice(1);

  {
    for (let i = 0; i < eventsMinimalCommonLength; i++) {
      const event = firstEventsFromPack[i];
      for (const otherEvents of restOfEventsPack) {
        if (!isEqual(event, otherEvents[i])) {
          diffStartedAt = Math.min(
            diffStartedAt,
            event.start,
            otherEvents[i].start,
          );
        }
      }
      if (diffStartedAt !== Infinity) {
        break;
      }
    }
    for (const longerEvents of eventsPack) {
      if (longerEvents.length > eventsMinimalCommonLength) {
        diffStartedAt = Math.min(
          diffStartedAt,
          longerEvents[eventsMinimalCommonLength].start,
        );
      }
    }
  }

  if (diffStartedAt === Infinity) {
    return null;
  }

  let diffEndedAt = -Infinity;
  {
    for (let i = 0; i < eventsMinimalCommonLength; i++) {
      const event = firstEventsFromPack.at(-i - 1)!;
      for (const otherEvents of restOfEventsPack) {
        const otherEvent = otherEvents.at(-i - 1)!;
        if (!isEqual(event, otherEvent)) {
          diffEndedAt = Math.max(diffEndedAt, event.end, otherEvent.end);
        }
      }
      if (diffEndedAt !== -Infinity) {
        break;
      }
    }

    for (const longerEvents of eventsPack) {
      if (longerEvents.length > eventsMinimalCommonLength) {
        diffEndedAt = Math.max(
          diffEndedAt,
          longerEvents.at(-eventsMinimalCommonLength - 1)!.end,
        );
      }
    }
  }

  return [diffStartedAt, diffEndedAt];
}

export type VoyageRange = [startTime: number, endTime: number];

export function getBallastToBallastRanges(eventsAndLegs: EventOrLeg<Events>[]) {
  const result = [] as VoyageRange[];

  // Pretty dump implementation for getting voyage ranges
  // Currently assuming as voyage everything outside of ballast movement

  let voyageStarted: undefined | number = undefined;
  for (const event of eventsAndLegs) {
    const isBallast = isLeg(event) && !event.laden;
    if (isBallast) {
      if (voyageStarted) {
        result.push([voyageStarted, event.start]);
      }
      voyageStarted = event.end;
    } else {
      if (voyageStarted === undefined) {
        voyageStarted = event.start;
      }
    }
  }

  const lastEvent = eventsAndLegs.at(-1);
  if (voyageStarted !== undefined && lastEvent) {
    result.push([voyageStarted, lastEvent.end]);
  }

  return result;
}

export function mergeAndSortRanges(ranges: VoyageRange[]) {
  const START = 1;
  const END = -1;
  const events = ranges
    .flatMap(
      (range) =>
        [
          [range[0], START],
          [range[1], END],
        ] as const,
    )
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const result: VoyageRange[] = [];
  if (events.length === 0) {
    return result;
  }

  let started: number | undefined = undefined;
  let voyagesCount = 0;
  for (const event of events) {
    const [time, action] = event;
    if (action === START) {
      if (voyagesCount === 0) {
        started = time;
      }
      voyagesCount++;
    } else if (action === END) {
      voyagesCount--;
    } else {
      exhaustiveCheck(action);
    }
    if (voyagesCount < 0) {
      throw new Error(`Internal error`);
    }
    if (voyagesCount === 0) {
      if (started === undefined) {
        throw new Error('Internal error');
      }
      if (started > time) {
        throw new Error(`Internal error`);
      }
      if (started !== time) {
        result.push([started, time]);
        const thisEv = result.at(-1);
        const prevEv = result.at(-2);
        if (thisEv && prevEv && prevEv[1] === thisEv[0]) {
          result.pop();
          result.pop();
          result.push([prevEv[0], thisEv[1]]);
        }
      }
    }
  }
  return result;
}

export function fitRangeOutsideOfVoyages(
  min: number,
  max: number,
  voyagesRanges: VoyageRange[],
): [number, number] {
  const merged = mergeAndSortRanges(voyagesRanges);
  for (const [startTime, endTime] of merged) {
    if (min > startTime && min < endTime) {
      min = startTime;
    }
    if (max > startTime && max < endTime) {
      max = endTime;
    }
  }

  // Find the start of the ballast leg before the first voyage range that is inside
  // the original min-max range
  if (merged.length > 0) {
    const earlierBallastStart = merged.findLast((range) => range[1] <= min);
    if (earlierBallastStart) {
      min = earlierBallastStart[1];
    }
  }

  return [min, max];
}
